import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { getUniqueIdMap } from "../../../../common/generateUniqueId";
import { env } from "../../../../env/env";

import { selectFetchLogicsStatus } from "../../../logics/logicsSlice";
import { createWindow } from "../../../windowManager/windowManagerSlice";
import { closeMenu } from "../../menuManagerSlice";
import { MenuContent, menuItem, menuSeparator } from "../Menu";

import SaveFormat from "../../../../common/save/SaveFormat";
import Version from "../../../../common/Version";
import { useTranslation } from "react-i18next";
import { selectEditor } from "../../../editor/editorSlice";

export type ProjectMenuType = "project";

export const saveVersion: Version = {
	commit: env.frontendVersion + env.branch,
	major: 1,
	minor: 0,
	patch: 0,
};

export function ProjectMenu() {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const logicsStatus = useAppSelector(selectFetchLogicsStatus);
	const editor = useAppSelector(selectEditor);

	return (
		<MenuContent
			parts={[
				menuItem({
					label: t("New"),
					effect: () => {
						dispatch(closeMenu());
						const windowSize = { x: 400, y: 160 };
						dispatch(
							createWindow({
								title: "New project confirmation",
								size: windowSize,
								position: {
									x: (window.innerWidth - windowSize.x) / 2,
									y: (window.innerHeight - windowSize.y) / 2,
								},
								typeAndProps: {
									type: "confirmNew",
									props: {},
								},
							}),
						);
					},
					disabled: logicsStatus.code !== "succeeded",
				}),
				menuItem({
					label: t("Load"),
					effect: () => {
						dispatch(closeMenu());

						const windowSize = { x: 500, y: 160 };

						dispatch(
							createWindow({
								title: "Load Project",
								size: windowSize,
								position: {
									x: (window.innerWidth - windowSize.x) / 2,
									y: (window.innerHeight - windowSize.y) / 2,
								},
								typeAndProps: {
									type: "loadProject",
									props: {},
								},
							}),
						);
					},
				}),
				menuItem({
					label: t("Save"),
					effect: () => {
						dispatch(closeMenu());

						const windowSize = { x: 500, y: 160 };

						dispatch(
							createWindow({
								title: "Save Project",
								size: windowSize,
								position: {
									x: (window.innerWidth - windowSize.x) / 2,
									y: (window.innerHeight - windowSize.y) / 2,
								},
								typeAndProps: {
									type: "saveProject",
									props: {},
								},
							}),
						);
					},
					disabled: logicsStatus.code !== "succeeded",
				}),
				menuItem({
					label: t("Export To JSON"),
					effect: () => {
						dispatch(closeMenu());
						console.log("Save");

						if (logicsStatus.code !== "succeeded") {
							throw new Error("Logics not loaded");
						}

						const saveFormat: SaveFormat = {
							logicsCommitRef: logicsStatus.logics.commit,
							editor,
							saveVersion,
							idMap: getUniqueIdMap(),
						};

						const json = JSON.stringify(saveFormat);

						function download(
							content: any,
							fileName: string,
							contentType: string,
						) {
							let a = document.createElement("a");
							let file = new Blob([content], { type: contentType });
							a.href = URL.createObjectURL(file);
							a.download = fileName;
							a.click();
						}

						download(json, `${editor.projectName}.json`, "blob");
					},
					disabled: logicsStatus.code !== "succeeded",
				}),
				menuSeparator(),
				menuItem({
					label: t("Migrate Logics Version"),
					effect: () => {
						dispatch(closeMenu());

						const windowSize = { x: 500, y: 160 };

						dispatch(
							createWindow({
								title: "Logics migration",
								size: windowSize,
								position: {
									x: (window.innerWidth - windowSize.x) / 2,
									y: (window.innerHeight - windowSize.y) / 2,
								},
								typeAndProps: {
									type: "logicsMigration",
									props: {},
								},
							}),
						);
					},
					disabled: logicsStatus.code !== "succeeded",
				}),
			]}
		/>
	);
}
